import { useEffect, useMemo, useState } from "react";
import { Heart } from "lucide-react";
import { api } from "@/lib/api";

const VISITOR_KEY = "tattoogen-matsi-like-visitor";

const getVisitorId = () => {
  try {
    const existing = window.localStorage.getItem(VISITOR_KEY);
    if (existing) return existing;
    const created = window.crypto?.randomUUID?.() || `matsi-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(VISITOR_KEY, created);
    return created;
  } catch {
    return `matsi-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

export default function MatsiLikeCard({ setId, selectedSlots }) {
  const [count, setCount] = useState(0);
  const [liked, setLiked] = useState(false);
  const [busy, setBusy] = useState(false);
  const visitorId = useMemo(() => getVisitorId(), []);

  const ready = !!setId && !!selectedSlots?.top && !!selectedSlots?.middle && !!selectedSlots?.bottom;

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    api.get("/public/likes", {
      params: {
        set_id: setId,
        top: selectedSlots.top,
        middle: selectedSlots.middle,
        bottom: selectedSlots.bottom,
        visitor_id: visitorId,
      },
    }).then((response) => {
      if (cancelled) return;
      setCount(response.data?.count || 0);
      setLiked(!!response.data?.liked);
    }).catch(() => {
      if (!cancelled) {
        setCount(0);
        setLiked(false);
      }
    });
    return () => { cancelled = true; };
  }, [ready, setId, selectedSlots?.top, selectedSlots?.middle, selectedSlots?.bottom, visitorId]);

  const toggleLike = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      const response = await api.post("/public/likes/toggle", {
        set_id: setId,
        top: selectedSlots.top,
        middle: selectedSlots.middle,
        bottom: selectedSlots.bottom,
        visitor_id: visitorId,
      });
      setCount(response.data?.count || 0);
      setLiked(!!response.data?.liked);
    } finally {
      setBusy(false);
    }
  };

  if (!ready) return null;

  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 14,
      padding: "12px 14px",
      marginTop: 12,
      border: "1px solid rgba(214,79,120,.45)",
      background: "rgba(18,20,21,.86)",
      borderRadius: 12,
    }}>
      <div style={{ minWidth: 0 }}>
        <strong style={{ display: "block", color: "#fff", fontSize: 14 }}>Gefällt dir diese Kombination?</strong>
        <span style={{ display: "block", marginTop: 3, color: "#bfc4c2", fontSize: 12 }}>Mit deinem Herz sieht Matsi, welche Ideen besonders gut ankommen.</span>
      </div>
      <button
        type="button"
        onClick={toggleLike}
        disabled={busy}
        aria-pressed={liked}
        aria-label={liked ? "Herz entfernen" : "Herz geben"}
        data-testid="btn-matsi-like"
        style={{
          flex: "0 0 auto",
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          minWidth: 68,
          justifyContent: "center",
          padding: "9px 11px",
          borderRadius: 999,
          border: liked ? "1px solid #ef7ca1" : "1px solid #555b5d",
          background: liked ? "rgba(214,79,120,.2)" : "rgba(255,255,255,.04)",
          color: liked ? "#ff9fbd" : "#f2f3f1",
          cursor: busy ? "wait" : "pointer",
          fontWeight: 700,
        }}
      >
        <Heart size={18} fill={liked ? "currentColor" : "none"} />
        <span>{count}</span>
      </button>
    </div>
  );
}
